'use client'

import { Image } from '@macarongfactory/web-ui'
import Link from 'next/link'
import { useEffect, useRef } from 'react'

import { ROUTES } from '@/shared/lib/routes'
import { OptimizedImage } from '@/shared/ui'

import { SWIPER_HORIZONTAL_PADDING_STYLE } from '../lib'
import useTrackOnVisible from '../model/useTrackOnVisible'

/** 카드 비율은 4:5 (가로:세로) — DriveMagazineSwiper 등 다른 swiper와 동일 */
const SLIDE_ASPECT_NUMERATOR = 5
const SLIDE_ASPECT_DENOMINATOR = 4
const SLIDE_WIDTH = 200
const SLIDE_HEIGHT =
  (SLIDE_WIDTH * SLIDE_ASPECT_NUMERATOR) / SLIDE_ASPECT_DENOMINATOR
/** 카드 사이 간격(px) */
const SLIDE_GAP = 12
/** onFirstSwipe 트리거 임계값(px) — 미세 스크롤 노이즈 무시 */
const FIRST_SCROLL_THRESHOLD = 4

export interface IStoryCardItem {
  id: number
  imageUrl: string
  title: string
  subtitle: string
}

type TStoryCardEmphasis = 'title' | 'subtitle'

interface IStoryCardSwiperProps {
  items: IStoryCardItem[]
  emphasis?: TStoryCardEmphasis
  /** 카드 노출 시 호출 (1회) */
  onCardShow?: (item: IStoryCardItem, index: number) => void
  /** 카드 클릭 시 호출 (기본 콘텐츠 상세 Link 동작과 함께 트래킹용) */
  onCardClick?: (item: IStoryCardItem, index: number) => void
  /**
   * 카드 선택 시 네비게이션을 직접 처리. 지정 시 콘텐츠 상세 Link 대신
   * 버튼으로 렌더되어 이 핸들러만 호출된다 (예: 정비소 인앱/외부 분기).
   */
  onCardSelect?: (item: IStoryCardItem, index: number) => void
  /** 첫 스크롤(슬라이드 변경) 시 호출 (1회) */
  onFirstSwipe?: () => void
}

const StoryCardSwiper = ({
  items,
  emphasis = 'title',
  onCardShow,
  onCardClick,
  onCardSelect,
  onFirstSwipe,
}: IStoryCardSwiperProps) => {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const hasScrolledRef = useRef(false)

  useEffect(
    function observeFirstScroll() {
      if (!onFirstSwipe) return
      const node = scrollRef.current
      if (!node) return

      const handleScroll = () => {
        if (hasScrolledRef.current) return
        if (node.scrollLeft < FIRST_SCROLL_THRESHOLD) return
        hasScrolledRef.current = true
        onFirstSwipe()
      }

      node.addEventListener('scroll', handleScroll, { passive: true })
      return () => node.removeEventListener('scroll', handleScroll)
    },
    [onFirstSwipe]
  )

  return (
    <div
      ref={scrollRef}
      className='scrollbar-hide flex overflow-x-auto'
      style={{ ...SWIPER_HORIZONTAL_PADDING_STYLE, gap: `${SLIDE_GAP}px` }}
    >
      {items.map((item, index) => (
        <div
          key={item.id}
          className='shrink-0'
          style={{ width: `${SLIDE_WIDTH}px` }}
        >
          <StoryCard
            item={item}
            emphasis={emphasis}
            index={index}
            onShow={onCardShow}
            onClick={onCardClick}
            onSelect={onCardSelect}
          />
        </div>
      ))}
    </div>
  )
}

const StoryCard = ({
  item,
  emphasis,
  index,
  onShow,
  onClick,
  onSelect,
}: {
  item: IStoryCardItem
  emphasis: TStoryCardEmphasis
  index: number
  onShow?: (item: IStoryCardItem, index: number) => void
  onClick?: (item: IStoryCardItem, index: number) => void
  onSelect?: (item: IStoryCardItem, index: number) => void
}) => {
  const cardRef = useRef<HTMLElement | null>(null)
  const setCardRef = (node: HTMLElement | null) => {
    cardRef.current = node
  }
  useTrackOnVisible({
    ref: cardRef,
    tracker: () => onShow?.(item, index),
    enabled: !!onShow,
  })

  const titleClassName =
    emphasis === 'title'
      ? 'text-title-2-semibold text-foreground-static-white line-clamp-2 max-h-dimension-x11 whitespace-pre-line'
      : 'text-title-2-semibold text-foreground-static-white truncate'

  const subtitleClassName =
    emphasis === 'title'
      ? 'text-caption-1-medium text-foreground-static-white truncate opacity-75'
      : 'text-body-2-medium text-foreground-static-white line-clamp-2'

  const cardClassName =
    'rounded-dimension-x5 relative block w-full cursor-pointer overflow-hidden text-left'

  const cardContent = (
    <>
      <OptimizedImage
        src={item.imageUrl}
        alt={item.title}
        width={SLIDE_WIDTH}
        height={SLIDE_HEIGHT}
        radius={0}
        scaletype='crop'
        sizes={`${SLIDE_WIDTH}px`}
        fallback={<Image.Fallback />}
      />
      {/* 그라데이션 영역은 타이틀 2줄 기준 높이(96px)로 고정해 타이틀 줄 수와
          무관하게 동일한 어둠 영역을 유지하고, 콘텐츠는 하단 정렬한다. */}
      <div className='gap-y-dimension-x1 p-dimension-x4 absolute bottom-0 left-0 flex min-h-[96px] w-full flex-col justify-end bg-gradient-to-t from-black/60 to-transparent'>
        <p className={titleClassName}>{item.title}</p>
        <p className={subtitleClassName}>{item.subtitle}</p>
      </div>
    </>
  )

  if (onSelect) {
    return (
      <button
        ref={setCardRef}
        type='button'
        onClick={() => onSelect(item, index)}
        className={cardClassName}
      >
        {cardContent}
      </button>
    )
  }

  return (
    <Link
      ref={setCardRef}
      href={ROUTES.CAR_STORY.CONTENT_DETAIL(item.id)}
      onClick={() => onClick?.(item, index)}
      className={cardClassName}
    >
      {cardContent}
    </Link>
  )
}

export default StoryCardSwiper
